/**
 * The platform live-invalidation registry's vocabulary (spec 2026-09-25-001
 * §5.4 and §5.5).
 *
 * A TABLE IS ITS SCHEMA-QUALIFIED SQL NAME (`public.clients`,
 * `insurance_broker_bms.client_addresses`, `cova_files.nodes`): the one name the ledger,
 * the change table and `governed-tables.ts` share. Anything else, a BMS
 * camelCase key included, is translated before it reaches the store.
 */
import type { QueryClient } from '@tanstack/react-query'

/** A schema-qualified table name, `schema.table`. */
export type TableName = string

/** A query-key root. React Query matches by prefix, so a root stales every key under it. */
export type QueryRoot = ReadonlyArray<unknown>

/** One generated seed row: a table and the roots known to read it. */
export interface SeedRow {
  readonly table: TableName
  readonly roots: ReadonlyArray<QueryRoot>
}

/** A root the feed cannot name by table (research 08 §3), and its class. */
export interface UnservableRoot {
  readonly root: string
  readonly reason: string
}

/** The only part of a `QueryClient` the registry calls. */
export type InvalidatingClient = Pick<QueryClient, 'invalidateQueries'>

/** What one `apply` did, so the poller can count what the registry ignored. */
export interface ApplyResult {
  /** Roots handed to the throttle (fired now, or held for the trailing edge). */
  readonly roots: number
  /** Tables neither seeded nor registered, and not access tables: ignored. */
  readonly unknownTables: ReadonlyArray<TableName>
  /** True when the page named an access table, so identity was refetched first. */
  readonly access: boolean
}

/**
 * The five access tables (spec ruling 9, research 08 §3). A write to one can
 * change what the viewer may see on every list, so it is answered by
 * refetching identity and then the access roots, never through `rootsFor`.
 */
export const ACCESS_TABLES: ReadonlySet<TableName> = new Set([
  'public.org_members',
  'public.organisations',
  'public.resource_access',
  'public.team_memberships',
  'public.teams',
])

/**
 * `IDENTITY_QUERY_KEY` from `src/lib/providers/identity/identity-provider.tsx`,
 * restated because `src/shared` does not import app providers. Each scope
 * suffixes it, so a prefix invalidation reaches every scope. The registry test
 * pins the two together.
 */
export const IDENTITY_QUERY_PREFIX = ['identity', 'current'] as const

/** The per-(client, root) minimum interval between invalidations (spec ruling 6). */
export const THROTTLE_INTERVAL_MS = 5_000
